import notificationsServiceInstance from "../logic/Notifications/notifications.service";
import { toast } from "react-toastify";
import { env } from "../config/env";
import i18n from "../i18n";

/** Localized push-toast copy. This is a non-React util, so it reads the
 *  i18n instance directly rather than via the `useTranslation` hook. */
const pt = (key: string, opts?: Record<string, unknown>): string =>
  i18n.t(key, { ns: "common", ...opts });

const VAPID_KEY_RAW = env.VAPID_PUBLIC_KEY;

/**
 * Convert the VAPID public key (URL-safe base64) to a Uint8Array.
 * `pushManager.subscribe` requires an ArrayBuffer/Uint8Array — passing
 * the raw string works in some browsers but is technically out of spec.
 */
function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = window.atob(base64);
  const output = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; i++) {
    output[i] = rawData.charCodeAt(i);
  }
  return output;
}

function getVapidKey(): Uint8Array | null {
  if (!VAPID_KEY_RAW) return null;
  try {
    return urlBase64ToUint8Array(VAPID_KEY_RAW);
  } catch {
    return null;
  }
}

/**
 * True if the existing browser subscription's applicationServerKey
 * matches the currently-configured VAPID public key. When this drifts
 * (e.g. backend rotated keys or the VAPID `subject` changed from
 * betright.ro to betright.app), the subscription is silently dead and
 * we must drop it and re-subscribe.
 */
function subscriptionMatchesCurrentVapid(
  subscription: PushSubscription,
  currentVapid: Uint8Array,
): boolean {
  const existing = subscription.options?.applicationServerKey;
  if (!existing) return false;
  const existingBytes = new Uint8Array(existing as ArrayBuffer);
  if (existingBytes.byteLength !== currentVapid.byteLength) return false;
  for (let i = 0; i < existingBytes.byteLength; i++) {
    if (existingBytes[i] !== currentVapid[i]) return false;
  }
  return true;
}

async function subscribeFresh(
  registration: ServiceWorkerRegistration,
  vapidKey: Uint8Array,
): Promise<PushSubscription> {
  return registration.pushManager.subscribe({
    userVisibleOnly: true,
    // Cast: TS 5.x lib.dom narrows `BufferSource` to require an
    // ArrayBuffer-backed Uint8Array, but PushSubscriptionOptionsInit
    // historically accepts any Uint8Array (and the spec is unchanged).
    // The cast is safe — the runtime contract didn't change.
    applicationServerKey: vapidKey as BufferSource,
  });
}

export async function registerForPushNotifications() {
  if (!("serviceWorker" in navigator) || !("PushManager" in window)) return;

  const registration = await navigator.serviceWorker.register("/sw.js");
  await navigator.serviceWorker.ready;

  const vapidKey = getVapidKey();
  if (!vapidKey) {
    // No VAPID configured for this build — nothing to do. Don't toast,
    // since this is a deployment concern, not a user-facing failure.
    return;
  }

  let subscription = await registration.pushManager.getSubscription();

  // Detect VAPID drift: if the user has an existing subscription whose
  // server key no longer matches our deployed VAPID, the backend can't
  // deliver to it. Drop it and re-subscribe so the user actually
  // receives notifications post-key-rotation.
  if (subscription && !subscriptionMatchesCurrentVapid(subscription, vapidKey)) {
    try {
      await subscription.unsubscribe();
    } catch {
      // Best-effort cleanup — fall through and try to subscribe fresh.
    }
    subscription = null;
  }

  if (subscription) {
    await notificationsServiceInstance.subscribe(subscription);
    return;
  }

  if (Notification.permission === "default") {
    // No on-load opt-in prompt. The loud full-width toast banner was
    // permission-fatigue and off-design; users enable push on demand from
    // Profile → settings (PushNotificationsButton runs the same
    // requestPermission + subscribe flow).
    return;
  }

  if (Notification.permission === "granted") {
    subscription = await subscribeFresh(registration, vapidKey);
    await notificationsServiceInstance.subscribe(subscription);
    toast.success(pt("chrome.push.activated"));
    return;
  }

  toast.warn(pt("chrome.push.deniedManual"));
}

/**
 * Best-effort full disable: tell the backend to drop the subscription
 * AND tell the browser to release it. The old implementation only did
 * the former, leaving a dead subscription cached in the service worker.
 */
export async function unsubscribeFromPush() {
  if (!("serviceWorker" in navigator)) return;
  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    if (subscription) {
      try {
        await notificationsServiceInstance.unsubscribe(subscription);
      } catch {
        // Backend call is best-effort during logout — never block UX.
      }
      try {
        await subscription.unsubscribe();
      } catch {
        // Browser-side cleanup is best-effort too.
      }
    }
  } catch {
    // navigator.serviceWorker.ready can reject if SW was never registered.
  }
}

/**
 * Returns whether the user currently has an active push subscription
 * tied to this build's VAPID key. Used by the profile toggle UI.
 */
export async function isPushEnabled(): Promise<boolean> {
  if (!("serviceWorker" in navigator) || !("PushManager" in window)) return false;
  const vapidKey = getVapidKey();
  if (!vapidKey) return false;
  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    if (!subscription) return false;
    return subscriptionMatchesCurrentVapid(subscription, vapidKey);
  } catch {
    return false;
  }
}

/**
 * User-initiated enable from a settings toggle. Differs from
 * registerForPushNotifications in that it never shows the soft prompt
 * toast — the user already affirmed by toggling, so we go straight to
 * the browser permission prompt if needed.
 */
export async function enablePushNotifications(): Promise<boolean> {
  if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
    toast.warn(pt("chrome.push.notSupported"));
    return false;
  }
  const vapidKey = getVapidKey();
  if (!vapidKey) {
    toast.error(pt("chrome.push.notConfigured"));
    return false;
  }

  if (Notification.permission === "denied") {
    toast.warn(pt("chrome.push.deniedSettings"));
    return false;
  }

  const registration = await navigator.serviceWorker.register("/sw.js");
  await navigator.serviceWorker.ready;

  if (Notification.permission === "default") {
    const perm = await Notification.requestPermission();
    if (perm !== "granted") {
      toast.info(pt("chrome.push.denied"));
      return false;
    }
  }

  // If a stale subscription exists with the wrong VAPID key, replace it.
  let subscription = await registration.pushManager.getSubscription();
  if (subscription && !subscriptionMatchesCurrentVapid(subscription, vapidKey)) {
    try {
      await subscription.unsubscribe();
    } catch {
      /* ignore */
    }
    subscription = null;
  }
  if (!subscription) {
    subscription = await subscribeFresh(registration, vapidKey);
  }
  await notificationsServiceInstance.subscribe(subscription);
  toast.success(pt("chrome.push.enabled"));
  return true;
}

/** User-initiated disable. */
export async function disablePushNotifications(): Promise<void> {
  await unsubscribeFromPush();
  toast.success(pt("chrome.push.disabled"));
}
